import { useEffect, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { BLOCKS, type BlockDef } from './blocks'
import { BlockAskForm } from './BlockAskForm'
import { LinkPicker } from './LinkPicker'
import type { State } from '../api'

/**
 * Formatting for the current selection, and an insert menu for blocks.
 *
 * The insert menu reads the same list as the '/' menu, so a block reachable one
 * way is always reachable the other.
 */
export function Toolbar({
  editor,
  disabled,
  onComment,
  state,
  branch,
}: {
  editor: Editor
  disabled: boolean
  /**
   * The pages this site has, so a link can be made by naming one.
   *
   * Optional because the toolbar is drawn in places that have no tree — and a
   * link bar that only takes a typed address is what this replaced, so its
   * absence degrades to exactly the old behaviour rather than to nothing.
   */
  state?: State
  branch?: string | null
  /**
   * Hand the current selection to whoever owns the conversation. The quote
   * travels with a little text either side of it, which is what lets a
   * comment find its place again after the page is edited — see
   * packages/core/src/comments.ts.
   */
  onComment?: (selection: { quote: string; prefix: string; suffix: string }) => void
}) {
  const [insertOpen, setInsertOpen] = useState(false)
  /**
   * The address being typed, or null while the link bar is closed.
   *
   * This was `window.prompt('Link target', 'https://')`, and linking is the
   * most-pressed control in this toolbar. A prompt cannot be styled, covers
   * the words being linked, carries no rule — the default `https://` pushed
   * everybody towards an external address when most links in documentation
   * are a page in the same documentation — and validates nothing. A browser
   * driving the editor can only dismiss it, so nothing had ever linked
   * anything here.
   *
   * It also could not EDIT one. Pressing Link on an existing link removed it,
   * so changing an address meant deleting the link and making it again, and
   * the removal itself only worked when the link was selected: with the cursor
   * merely inside one, `unsetMark` had no range to act on and did nothing at
   * all. Both are `extendMarkRange` now, which is what makes "the link the
   * cursor is in" a range.
   */
  const [linkTo, setLinkTo] = useState<string | null>(null)
  /**
   * A block chosen from Insert that has to ask something first.
   *
   * On the toolbar's own row, beside the link bar and for the same reasons —
   * and NOT inside the Insert popover, which closes on `mouseleave`: a form
   * that disappears when the pointer travels to its own button is worse than
   * the prompt it replaces.
   */
  const [asking, setAsking] = useState<BlockDef | null>(null)

  /**
   * Re-render when the SELECTION moves, not only when the document changes.
   *
   * Every control here reads editor state — which mark is active, whether
   * anything is selected — and a toolbar that only repaints on document
   * changes shows yesterday's answer: the Comment button stayed greyed out
   * after selecting words, which is a control lying about what it can do.
   */
  const [, repaint] = useState(0)
  useEffect(() => {
    const tick = () => repaint((n) => n + 1)
    editor.on('selectionUpdate', tick)
    editor.on('transaction', tick)
    return () => {
      editor.off('selectionUpdate', tick)
      editor.off('transaction', tick)
    }
  }, [editor])

  /** The address of the link the cursor is in, or an empty field. */
  const currentHref = () => String(editor.getAttributes('link')['href'] ?? '')

  /**
   * Whether there is anything for a link to sit ON.
   *
   * With nothing selected and no link under the cursor, `setMark` leaves a
   * stored mark — the NEXT thing typed becomes a link — which looks from the
   * outside like a button that did nothing. The bar says what to do instead of
   * accepting an answer it cannot honour.
   */
  const somethingToLink = !editor.state.selection.empty || editor.isActive('link')

  const applyLink = () => {
    const href = (linkTo ?? '').trim()
    if (href === '' || !somethingToLink) return
    editor.chain().focus().extendMarkRange('link').setMark('link', { href }).run()
    setLinkTo(null)
  }

  const mark = (name: string, label: string, title: string) => (
    <button
      key={name}
      className={`tb__btn${editor.isActive(name) ? ' is-on' : ''}`}
      disabled={disabled}
      data-tip={title}
      onClick={() => editor.chain().focus().toggleMark(name).run()}
      type="button"
    >
      {label}
    </button>
  )

  const blockButton = (node: string, attrs: Record<string, unknown>, label: string) => (
    <button
      key={label}
      className={`tb__btn${editor.isActive(node, attrs) ? ' is-on' : ''}`}
      disabled={disabled}
      onClick={() => editor.chain().focus().setNode(node, attrs).run()}
      type="button"
    >
      {label}
    </button>
  )

  return (
    <div className="tb">
      {blockButton('paragraph', {}, 'P')}
      {blockButton('heading', { level: 1 }, 'H1')}
      {blockButton('heading', { level: 2 }, 'H2')}
      {blockButton('heading', { level: 3 }, 'H3')}
      <span className="tb__sep" />
      {mark('strong', 'B', 'Bold')}
      {mark('em', 'I', 'Italic')}
      {mark('strike', 'S', 'Strikethrough')}
      {mark('underline', 'U', 'Underline')}
      {mark('highlight', '◧', 'Highlight')}
      {mark('code', '‹›', 'Inline code')}
      <button
        className={`tb__btn${editor.isActive('link') ? ' is-on' : ''}`}
        disabled={disabled}
        type="button"
        onClick={() => {
          setAsking(null)
          setLinkTo(linkTo === null ? currentHref() : null)
        }}
      >
        Link
      </button>
      {onComment && (
        <button
          className="tb__btn"
          disabled={disabled || editor.state.selection.empty}
          data-tip={
            editor.state.selection.empty
              ? 'Select the words you want to ask about'
              : 'Comment on this selection'
          }
          type="button"
          onClick={() => {
            const { from, to } = editor.state.selection
            const quote = editor.state.doc.textBetween(from, to, ' ')
            if (!quote.trim()) return
            onComment({
              quote,
              prefix: editor.state.doc.textBetween(Math.max(0, from - 32), from, ' '),
              suffix: editor.state.doc.textBetween(
                to,
                Math.min(editor.state.doc.content.size, to + 32),
                ' ',
              ),
            })
          }}
        >
          Comment
        </button>
      )}
      <span className="tb__sep" />
      <button
        className="tb__btn"
        disabled={disabled}
        type="button"
        onClick={() => editor.chain().focus().toggleList('bulletList', 'listItem').run()}
      >
        List
      </button>
      <button
        className="tb__btn"
        disabled={disabled}
        type="button"
        onClick={() => editor.chain().focus().toggleList('orderedList', 'listItem').run()}
      >
        1.
      </button>

      <span className="tb__sep" />
      <div className="tb__insert">
        <button
          className="tb__btn tb__btn--wide"
          disabled={disabled}
          type="button"
          onClick={() => setInsertOpen((v) => !v)}
        >
          + Insert
        </button>
        {insertOpen && (
          <div className="pop tb__menu" onMouseLeave={() => setInsertOpen(false)}>
            {BLOCKS.filter((b) => b.group !== 'Text').map((b) => (
              <button
                key={b.id}
                className="menu-item"
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault()
                  setInsertOpen(false)
                  // One row open at a time: the link bar and a block's question
                  // share the line under the buttons.
                  setLinkTo(null)
                  if (b.ask) return setAsking(b)
                  b.run(editor, '')
                }}
              >
                <span className="menu-item__body">
                  <span className="menu-item__label">{b.label}</span>
                  <span className="menu-item__hint">{b.hint}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <span className="tb__spacer" />
      <button
        className="tb__btn"
        disabled={disabled || !editor.can().undo()}
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        data-tip="Undo"
      >
        ↶
      </button>
      <button
        className="tb__btn"
        disabled={disabled || !editor.can().redo()}
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        data-tip="Redo"
      >
        ↷
      </button>
      <span className="tb__hint">
        press <kbd>/</kbd> for blocks
      </span>

      {/*
        The link bar, on its own line under the buttons rather than in a box
        the operating system drew over the words being linked. `.tb` wraps, so
        this is a row of the toolbar and the writing column below it does not
        move.
      */}
      {linkTo !== null && !disabled && state && (
        <div className="tb__link">
          <LinkPicker
            state={state}
            branch={branch ?? null}
            initial={linkTo}
            onClose={() => setLinkTo(null)}
            onChoose={({ href, text }) => {
              /*
               * Three cases, and the middle one is the one that was wrong.
               *
               * Something selected: the words stay and gain the link. The
               * cursor merely INSIDE an existing link: that link changes,
               * whole — which is what `extendMarkRange` is for, and what
               * pressing Link on a link means. Nothing selected and no link
               * under the cursor: the page's own name is inserted AS the
               * link, which is what typing `[[` means.
               */
              if (editor.state.selection.empty && !editor.isActive('link')) {
                editor
                  .chain()
                  .focus()
                  .insertContent([
                    { type: 'text', text, marks: [{ type: 'link', attrs: { href } }] },
                  ])
                  .run()
              } else {
                editor.chain().focus().extendMarkRange('link').setMark('link', { href }).run()
              }
              setLinkTo(null)
            }}
          />
          {editor.isActive('link') && (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetMark('link').run()
                setLinkTo(null)
              }}
            >
              Remove
            </button>
          )}
        </div>
      )}
      {linkTo !== null && !disabled && !state && (
        <form
          className="tb__link"
          onSubmit={(e) => {
            e.preventDefault()
            applyLink()
          }}
        >
          <label className="label" htmlFor="tb-link">
            Link to
          </label>
          <input
            id="tb-link"
            className="input mono"
            autoFocus
            value={linkTo}
            placeholder="/guides/install"
            aria-describedby="tb-link-note"
            onChange={(e) => setLinkTo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setLinkTo(null)
            }}
          />
          <button
            className="btn btn--sm btn--tinted"
            disabled={linkTo.trim() === '' || !somethingToLink}
          >
            Apply
          </button>
          {editor.isActive('link') && (
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetMark('link').run()
                setLinkTo(null)
              }}
            >
              Remove
            </button>
          )}
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setLinkTo(null)}>
            Cancel
          </button>
          <p className="field__note" id="tb-link-note">
            {somethingToLink
              ? 'A page in this documentation is the address a reader uses — /guides/install. Anything else takes its whole address, starting with https://.'
              : 'Select the words this link should sit on first.'}
          </p>
        </form>
      )}

      {/* The other row on that line: a block that has to ask something before
          it can be inserted. Same shape as the link bar, because it is the
          same act — one field, its rule, and two answers. */}
      {asking?.ask && !disabled && (
        <BlockAskForm
          ask={asking.ask}
          className="tb__link"
          onAnswer={(answer) => {
            asking.run(editor, answer)
            setAsking(null)
          }}
          onCancel={() => {
            setAsking(null)
            editor.chain().focus().run()
          }}
        />
      )}
    </div>
  )
}
